// Wires a session store to a session controller (contracts/session-seam.md §1; P-AUTH-4): the
// controller is the restorer, the credential provider and onUnauthenticated (renew once, shared),
// and its local clean-up runs with every other personal-data cleaner.
import {
  type ControllerDeps,
  createSessionController,
  type SessionController,
} from "./controller";
import { createSessionStore, type SessionStoreOptions } from "./store";
import type { PersonalDataCleaner, SessionStore } from "./types";

export type AppSession = { store: SessionStore; controller: SessionController };

export function createAppSession(
  deps: Omit<ControllerDeps, "store"> & {
    /** Cleaners of other features (001: the current school). */
    cleaners?: readonly PersonalDataCleaner[];
    storeOptions?: SessionStoreOptions;
  },
): AppSession {
  const { cleaners = [], storeOptions, ...controllerDeps } = deps;
  let controller: SessionController;
  const store = createSessionStore(
    {
      restorer: { restore: () => controller.restore() },
      credentials: { getAccessToken: () => controller.getAccessToken() },
      onUnauthenticated: () => {
        // A failed renewal either signed out (SESSION_ENDED) or left the user signed in (offline).
        void controller.renew().catch(() => {});
      },
      personalDataCleaners: [
        ...cleaners,
        (cause) => controller.clearLocal(cause),
      ],
    },
    storeOptions,
  );
  controller = createSessionController({
    ...controllerDeps,
    store: () => store,
  });
  return { store, controller };
}
